import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  TextField,
  FormControl,
  Select,
  InputLabel,
  OutlinedInput,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import UserAvatar from "components/UserAvatar";

import { backendGet } from "@accredo/fe-shared/services/dataService";

import { useSnackbar } from "notistack";

import { useBlogPostActions } from "../../../../hooks/useBlogPostActions.jsx";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";

export default function BlogPostSettings({
  blogPost,
  authors = [],
  onSave = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    updateBlogPostRoles,
    updateBlogPostCustomers,
    updateBlogPostAuthors,
    updateBlogPostTags,
    createBlogPostTag,
  } = useBlogPostActions();

  const roles = blogPost.roles || [];
  const customers = blogPost.customers || [];

  const [menuAnchor, setMenuAnchor] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);

  const [selectedAuthors, setSelectedAuthors] = useState(
    authors.length ? authors : blogPost.authors || []
  );

  const [selectedCustomers, setSelectedCustomers] = useState(customers);

  const [selectedRoles, setSelectedRoles] = useState(roles.map((r) => r.id));

  const [userOptions, setUserOptions] = useState([]);

  const [customerOptions, setCustomerOptions] = useState([]);

  const [rolesOptions, setRolesOptions] = useState([]);

  const [searchLoadingUsers, setSearchLoadingUsers] = useState(false);

  const [searchLoadingCustomers, setSearchLoadingCustomers] = useState(false);

  const [saving, setSaving] = useState(false);

  // Tags
  const [selectedTags, setSelectedTags] = useState(blogPost.tags || []);

  const [tagOptions, setTagOptions] = useState([]);

  const [searchLoadingTags, setSearchLoadingTags] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const displayedAuthors = useMemo(() => {
    if (authors?.length) {
      return authors;
    }

    return blogPost?.creator ? [blogPost.creator] : [];
  }, [authors, blogPost]);

  const displayedCustomers = useMemo(() => {
    if (customers?.length) {
      return customers;
    }

    return [];
  }, [customers]);

  /**
   * Menü öffnen
   */
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  /**
   * Modal öffnen
   */
  const handleOpenSettingsModal = () => {
    setSelectedAuthors(authors.length ? authors : blogPost.authors || []);

    setSelectedCustomers(customers || []);

    setSelectedRoles(roles.map((r) => r.id) || []);

    setSelectedTags(blogPost.tags || []);

    setModalOpen(true);

    setMenuAnchor(null);
  };

  const fetchRoles = async () => {
    try {
      const res = await backendGet(`data/permissions/roles`);

      setRolesOptions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * User Suche
   */
  const handleSearchUsers = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setUserOptions([]);

      return;
    }

    try {
      setSearchLoadingUsers(true);

      const res = await backendGet(
        `data/users?api_version=v2&_searchword=${encodeURIComponent(
          searchValue
        )}`
      );

      setUserOptions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoadingUsers(false);
    }
  };

  /**
   * Kunden Suche
   */
  const handleSearchCustomers = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setCustomerOptions([]);

      return;
    }

    try {
      setSearchLoadingCustomers(true);

      const res = await backendGet(
        `data/kundes?api_version=v2&_searchword=${encodeURIComponent(
          searchValue
        )}`
      );

      setCustomerOptions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoadingCustomers(false);
    }
  };

  // Tags Suche ergänzen
  const handleSearchTags = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setTagOptions([]);

      return;
    }

    try {
      setSearchLoadingTags(true);

      const res = await backendGet(
        `data/blog-tags?api_version=v2&_searchword=${encodeURIComponent(
          searchValue
        )}`
      );

      setTagOptions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoadingTags(false);
    }
  };

  /**
   * Alles speichern
   */
  const handleSave = async () => {
    try {
      setSaving(true);

      /**
       * Neue Tags erstellen
       */
      const preparedTags = await Promise.all(
        selectedTags.map(async (tag) => {
          /**
           * Bereits existierender Tag
           */
          if (typeof tag !== "string") {
            return tag;
          }

          /**
           * Neuer Tag
           */
          const res = await createBlogPostTag(tag);

          return res.data;
        })
      );

      await Promise.all([
        updateBlogPostAuthors(blogPost.id, {
          toSend: {
            authors: selectedAuthors.map((author) => author.id),
          },
        }),

        updateBlogPostCustomers(blogPost.id, {
          toSend: {
            customers: selectedCustomers.map((customer) => customer.id),
          },
        }),

        updateBlogPostRoles(blogPost.id, {
          toSend: {
            roles: selectedRoles,
          },
        }),

        updateBlogPostTags(blogPost.id, {
          toSend: {
            // tags: selectedTags.map((tag) => ({
            //   id: tag.id,
            //   name: typeof tag === "string" ? tag : tag.name,
            // })),
            tags: preparedTags.map((tag) => tag.id),
          },
        }),
      ]);

      enqueueSnackbar("Blog Einstellungen aktualisiert", {
        variant: "success",
      });

      setModalOpen(false);

      onSave();
    } catch (err) {
      enqueueSnackbar("Fehler beim Speichern", {
        variant: "error",
      });

      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <IconButton onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        size="small"
      >
        <MenuItem size="small" onClick={handleOpenSettingsModal}>
          Einstellungen bearbeiten
        </MenuItem>
      </Menu>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Blog Einstellungen verwalten</DialogTitle>

        <DialogContent>
          {/* Tags */}
          <Box mt={3}>
            <Typography variant="subtitle2" mb={1}>
              Tags
            </Typography>

            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              options={tagOptions}
              value={selectedTags}
              loading={searchLoadingTags}
              onInputChange={(_, value) => {
                handleSearchTags(value);
              }}
              onChange={(_, value) => {
                setSelectedTags(value);
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }

                return option.name || "";
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === "string" || typeof value === "string") {
                  return option === value;
                }

                return option.id === value.id;
              }}
              renderValue={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({
                      index,
                    })}
                    key={option.id || option}
                    label={typeof option === "string" ? option : option.name}
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  {option.name}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags hinzufügen"
                  placeholder="Tag suchen oder neu erstellen"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoadingTags ? (
                          <CircularProgress size={18} />
                        ) : null}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Autoren */}
          <Box mt={2}>
            <Typography variant="subtitle2" mb={1}>
              Autoren
            </Typography>

            <Autocomplete
              multiple
              filterSelectedOptions
              options={userOptions}
              value={selectedAuthors}
              loading={searchLoadingUsers}
              onInputChange={(_, value) => {
                handleSearchUsers(value);
              }}
              onChange={(_, value) => {
                setSelectedAuthors(value);
              }}
              getOptionLabel={(option) =>
                option.fullname || option.name || option.email || ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderValue={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({
                      index,
                    })}
                    key={option.id}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <UserAvatar user={option} />

                        <span>{option.fullname || option.email}</span>
                      </Box>
                    }
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <UserAvatar user={option} />

                  <Box>
                    <Typography variant="body2">
                      {option.fullname || option.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Autoren suchen"
                  placeholder="Name oder E-Mail"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoadingUsers ? (
                          <CircularProgress size={18} />
                        ) : null}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Kunden */}
          <Box mt={3}>
            <Typography variant="subtitle2" mb={1}>
              Kunden
            </Typography>

            <Autocomplete
              multiple
              filterSelectedOptions
              options={customerOptions}
              value={selectedCustomers}
              loading={searchLoadingCustomers}
              onInputChange={(_, value) => {
                handleSearchCustomers(value);
              }}
              onChange={(_, value) => {
                setSelectedCustomers(value);
              }}
              getOptionLabel={(option) =>
                option.display_name || option.email2 || ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({
                      index,
                    })}
                    key={option.id}
                    label={option.display_name}
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <UserAvatar user={option} />

                  <Box>
                    <Typography variant="body2">
                      {option.display_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                    Abbrechen
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Kunden suchen"
                  placeholder="Name oder E-Mail"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoadingCustomers ? (
                          <CircularProgress size={18} />
                        ) : null}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Rollen */}
          <Box mt={3}>
            <Typography variant="subtitle2" mb={1}>
              Rollen
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Rollen</InputLabel>

              <Select
                multiple
                value={selectedRoles}
                onChange={(event) => {
                  const {
                    target: { value },
                  } = event;

                  setSelectedRoles(
                    typeof value === "string" ? value.split(",") : value
                  );
                }}
                input={<OutlinedInput label="Rollen" />}
              >
                {rolesOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <DsButton onClick={() => setModalOpen(false)} variant="tertiary">
            Abbrechen
          </DsButton>

          <DsButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Speichern"}
          </DsButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
